import { useEffect, useState } from 'react'
import { AlertCircle, Copy, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { procedureTemplatesApi } from '@/api/procedureTemplatesApi'
import {
  buildCopyDraftFromDetail,
  buildCopyPayload,
  mapCopyError,
  setFormName,
  validateCopyDraft,
  type CopyDraft,
} from '@/lib/procedureTemplateCopy'
import { templateUniqueId, versionLabel } from '@/lib/versioning'
import type { ProcedureTemplateDetail, ProcedureTemplateListItem } from '@/types/cgt'

interface CopyProcedureTemplateDialogProps {
  open: boolean
  /** The specific VERSION row being copied — a family has no single flow to copy. */
  source: ProcedureTemplateListItem | null
  onOpenChange: (open: boolean) => void
  onCreated: (created: ProcedureTemplateDetail) => void
}

export function CopyProcedureTemplateDialog({
  open,
  source,
  onOpenChange,
  onCreated,
}: CopyProcedureTemplateDialogProps) {
  // The list row carries no sequence, so the dialog fetches the detail to learn
  // which forms it is about to copy. Null while that is in flight.
  const [draft, setDraft] = useState<CopyDraft | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // Re-seed on open so a cancelled copy never leaks into the next one.
  useEffect(() => {
    if (!open || !source) return
    let cancelled = false
    setDraft(null)
    setLoadError(null)
    setError(null)
    setFieldErrors({})
    setLoading(true)
    procedureTemplatesApi
      .get(source.id)
      .then(detail => {
        if (!cancelled) setDraft(buildCopyDraftFromDetail(detail))
      })
      .catch(e => {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : 'Failed to load the template.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, source])

  if (!source) return null

  const validation = draft ? validateCopyDraft(draft) : {}
  const canSubmit = !!draft && Object.keys(validation).length === 0 && !busy

  // A submit attempt shows validation; before that the dialog stays quiet so
  // the pre-filled defaults don't open under a wall of red.
  const shownErrors = { ...fieldErrors }

  async function handleCreate() {
    if (!draft || !source || Object.keys(validateCopyDraft(draft)).length > 0) {
      setFieldErrors(draft ? validateCopyDraft(draft) : {})
      return
    }
    setBusy(true)
    setError(null)
    setFieldErrors({})
    try {
      const created = await procedureTemplatesApi.copy(source.id, buildCopyPayload(draft))
      onCreated(created)
      onOpenChange(false)
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to create the copy.'
      const perField = mapCopyError(message, draft)
      setFieldErrors(perField)
      // Only surface the dialog-level line when no row claimed the message,
      // or the same text appears twice.
      if (Object.keys(perField).length === 0) setError(message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={o => !busy && onOpenChange(o)}>
      <DialogContent className='sm:max-w-2xl'>
        <DialogHeader>
          <DialogTitle>Copy Procedure Template</DialogTitle>
          <DialogDescription>
            Creates a brand-new procedure template and all of its forms, each with its own id and
            version family — the fields, sections and gates of &ldquo;{source.name}&rdquo;{' '}
            {versionLabel(source.version)} are duplicated, not linked.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className='flex items-center gap-2 py-6 text-sm text-muted-foreground'>
            <Loader2 className='h-4 w-4 animate-spin' />
            Loading the template&rsquo;s forms…
          </div>
        )}

        {loadError && (
          <p className='flex items-start gap-1.5 py-4 text-sm text-destructive'>
            <AlertCircle className='h-4 w-4 shrink-0 mt-0.5' />
            {loadError}
          </p>
        )}

        {draft && (
          <div className='max-h-[60vh] space-y-5 overflow-y-auto py-1 pr-1'>
            <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-start'>
              <div className='space-y-1'>
                <Label className='text-xs uppercase tracking-wide text-muted-foreground'>
                  Template name
                </Label>
                <p className='text-sm font-medium'>{source.name}</p>
                <p className='font-mono text-[11px] text-muted-foreground'>
                  {templateUniqueId(source)}
                </p>
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='copy-template-name'>New template name</Label>
                <Input
                  id='copy-template-name'
                  value={draft.name}
                  autoFocus
                  aria-invalid={!!shownErrors.template}
                  onChange={e => setDraft({ ...draft, name: e.target.value })}
                />
                {shownErrors.template && (
                  <p className='text-xs text-destructive'>{shownErrors.template}</p>
                )}
              </div>
            </div>

            {draft.forms.length > 0 ? (
              <div className='space-y-3'>
                <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
                  <Label className='text-xs uppercase tracking-wide text-muted-foreground'>
                    Form name
                  </Label>
                  {/* Sentence case to match "New template name" above — the
                      uppercase treatment marks the read-only source columns. */}
                  <Label className='hidden sm:block'>New form name</Label>
                </div>
                {draft.forms.map(form => (
                  <div
                    key={form.formDefinitionId}
                    className='grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-start'
                  >
                    <p className='text-sm sm:pt-2'>{form.sourceName}</p>
                    <div className='space-y-1.5'>
                      <Input
                        aria-label={`New name for ${form.sourceName}`}
                        value={form.name}
                        aria-invalid={!!shownErrors[form.formDefinitionId]}
                        onChange={e =>
                          setDraft(setFormName(draft, form.formDefinitionId, e.target.value))
                        }
                      />
                      {shownErrors[form.formDefinitionId] && (
                        <p className='text-xs text-destructive'>
                          {shownErrors[form.formDefinitionId]}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className='text-sm text-muted-foreground'>
                This version has no forms yet — the copy will be an empty template.
              </p>
            )}

            {error && (
              <p className='flex items-start gap-1.5 text-sm text-destructive'>
                <AlertCircle className='h-4 w-4 shrink-0 mt-0.5' />
                {error}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={!canSubmit}>
            {busy ? (
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            ) : (
              <Copy className='mr-2 h-4 w-4' />
            )}
            Create Copy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
